/**
 * BlockersChart Component
 *
 * Horizontal bar chart showing top blockers
 */

import React from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';

interface Blocker {
  label: string;
  count: number;
}

interface BlockersChartProps {
  blockers: Blocker[];
  maxItems?: number;
  onBlockerClick?: (blocker: string) => void;
  activeBlocker?: string | null;
}

const BlockersChart: React.FC<BlockersChartProps> = ({ blockers, maxItems = 5, onBlockerClick, activeBlocker }) => {
  const topBlockers = blockers.slice(0, maxItems);
  const maxCount = Math.max(...topBlockers.map(b => b.count), 1);
  const isClickable = !!onBlockerClick;

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', pr: 1 }}>
      {topBlockers.map((blocker, index) => {
        const percentage = (blocker.count / maxCount) * 100;
        const isActive = activeBlocker === blocker.label;

        return (
          <Box
            key={index}
            onClick={isClickable ? () => onBlockerClick(blocker.label) : undefined}
            sx={{
              mb: index === topBlockers.length - 1 ? 0 : 2,
              p: 1,
              borderRadius: 1,
              cursor: isClickable ? 'pointer' : 'default',
              transition: 'all 0.2s',
              bgcolor: isActive ? 'warning.light' : 'transparent',
              border: isActive ? '2px solid' : '2px solid transparent',
              borderColor: isActive ? 'warning.main' : 'transparent',
              '&:hover': isClickable ? {
                bgcolor: isActive ? 'warning.light' : 'action.hover',
              } : {},
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {blocker.label}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                {blocker.count}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={percentage}
              sx={{
                height: 8,
                borderRadius: 1,
                bgcolor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  bgcolor: 'warning.main',
                  borderRadius: 1,
                },
              }}
            />
          </Box>
        );
      })}
    </Box>
  );
};

export default BlockersChart;
